import { ImageStrip, type ImageStripItem } from "@prototypes/image-strip/ImageStrip";
import { ImageStripEmptyPlaceholder } from "@prototypes/image-strip/ImageStripEmptyPlaceholder";
import classNames from "classnames";
import { Plus, Search } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { useComponentState } from "../../utils/customHook.util";
import { Flex } from "../Common/Flex";
import Label from "../Common/Label";
import "./ImageSelector.css";
import { supportedImageAccept } from "../../common/utils/image.util";
import { takeSupportedImageFiles } from "../../utils/imageUpload.util";
import HandleDragAndDrop from "../HandleDragAndDrop";
import {
    type ExternalFileReference,
    getUrlFromReference,
    type ImageReference,
    isExternalReference,
    isLocalReference,
    type LocalFileReference,
} from "./ImageReference";
import ImageSearch from "./ImageSearch";

function arrangeLocalReferences(
    imageReferences: ImageReference[] | undefined,
    files: File[],
) {
    const existingFileNames = (imageReferences || [])
        .filter(isLocalReference)
        .map((it) => it.file.name);

    const newFiles: LocalFileReference[] = files
        .filter((file) => !existingFileNames.includes(file.name))
        .map((file) => ({
            type: "local",
            file,
        }));

    return [...(imageReferences || []), ...newFiles];
}

function arrangeExternalReferences(
    imageReferences: ImageReference[] | undefined,
    urls: string[] | undefined,
) {
    const existingUrls = (imageReferences || [])
        .filter(isExternalReference)
        .map((it) => it.url);

    const newUrls: ExternalFileReference[] = (urls || [])
        .filter((it) => !existingUrls.includes(it))
        .map((url) => ({
            type: "external",
            url,
        }));

    return [...(imageReferences || []), ...newUrls];
}

type SelectorStripItem = ImageStripItem & { reference: ImageReference };

function imageReferenceId(reference: ImageReference): string {
    switch (reference.type) {
        case "local":
            return `local:${reference.file.name}:${reference.file.size}:${reference.file.lastModified}`;
        case "library":
            return reference.file.id;
        case "external":
            return reference.url;
        default: {
            const exhaustive: never = reference;
            return exhaustive;
        }
    }
}

function toSelectorStripItems(
    imageReferences: ImageReference[] | undefined,
): SelectorStripItem[] {
    return (imageReferences ?? []).map((reference) => ({
        id: imageReferenceId(reference),
        src: getUrlFromReference(reference),
        alt: "",
        reference,
    }));
}

type ImageSelectorState = {
    externalImageSelectorOpen: boolean;
    imageReferences: ImageReference[] | undefined;
};
type ImageSelectorProps = {
    id: string;
    title: string;
    subtitle?: string;
    className?: string;
    initialImageReferences?: ImageReference[];
    googlePlaceId?: string;
    websiteUrl?: string;
    /** Local files are being uploaded. Those tiles show a spinner. */
    uploading?: boolean;
    isValid?: (imageReferences: ImageReference[] | undefined) => boolean;
    onChange: (imageReferences: ImageReference[] | undefined) => void;
};

function ImageSelector(props: ImageSelectorProps) {
    const {
        id,
        className,
        googlePlaceId,
        websiteUrl,
        isValid,
        onChange,
        title,
        subtitle,
        initialImageReferences,
        uploading = false,
    } = props;
    const { state, setState } = useComponentState<ImageSelectorState>({
        externalImageSelectorOpen: false,
        imageReferences: initialImageReferences,
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const imageCount = state.imageReferences?.length ?? 0;
    const stripImages = toSelectorStripItems(state.imageReferences);
    const imagesMissing = Boolean(isValid && !isValid(state.imageReferences));
    const busyIds = uploading
        ? stripImages
              .filter((item) => item.reference.type === "local")
              .map((item) => item.id)
        : [];
    const showSearchButton = !!(googlePlaceId || websiteUrl);

    function addLocalImages(files: FileList | null) {
        const newFiles = takeSupportedImageFiles(files);

        if (newFiles.length > 0) {
            const arrangedReferences = arrangeLocalReferences(
                state.imageReferences,
                newFiles,
            );
            setState({ imageReferences: arrangedReferences });
            if (isValid) {
                isValid(arrangedReferences);
            }
            onChange(arrangedReferences);
        }
    }

    function addExternalImages(selectedImages: string[]) {
        const arrangedReferences = arrangeExternalReferences(
            state.imageReferences,
            selectedImages,
        );
        setState({
            externalImageSelectorOpen: false,
            imageReferences: arrangedReferences,
        });
        if (isValid) {
            isValid(arrangedReferences);
        }
        onChange(arrangedReferences);
    }

    function applyImageReferences(imageReferences: ImageReference[] | undefined) {
        setState({ imageReferences });
        if (isValid) {
            isValid(imageReferences);
        }
        onChange(imageReferences);
    }

    function onStripReorder(next: SelectorStripItem[]) {
        applyImageReferences(
            next.length === 0 ? undefined : next.map((item) => item.reference),
        );
    }

    useEffect(() => {
        setState({ imageReferences: initialImageReferences });
    }, [id]);

    return (
        <HandleDragAndDrop
            label="Drag and drop images here..."
            className="image-selector__drag-and-drop"
            onDropFiles={(files) => {
                addLocalImages(files);
            }}
        >
            <Flex.Column
                gap="medium"
                className={classNames(
                    "image-selector",
                    {
                        "image-selector--invalid":
                            isValid && !isValid(state.imageReferences),
                    },
                    className,
                )}
            >
                <Flex.Row gap="medium" justifyContent="space-between">
                    <Flex.Column gap="xsmall">
                        <h4>{title}</h4>
                        {subtitle && (
                            <Label.Medium.Light.N50>{subtitle}</Label.Medium.Light.N50>
                        )}
                    </Flex.Column>
                    <Flex.Row gap="medium" justifyContent="end">
                        <Button
                            type="button"
                            onClick={() => {
                                fileInputRef.current?.click();
                            }}
                        >
                            <Plus data-icon="inline-start" />
                            Upload
                        </Button>
                        {showSearchButton && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setState({
                                        externalImageSelectorOpen: true,
                                    });
                                }}
                            >
                                <Search data-icon="inline-start" />
                                Search
                            </Button>
                        )}
                        {state.externalImageSelectorOpen && (
                            <ImageSearch
                                googlePlaceId={googlePlaceId}
                                websiteUrl={websiteUrl}
                                onClose={() => {
                                    setState({
                                        externalImageSelectorOpen: false,
                                    });
                                }}
                                onSave={(selected) => {
                                    addExternalImages(selected || []);
                                }}
                            />
                        )}
                    </Flex.Row>
                </Flex.Row>

                <Flex.Column>
                    {imageCount === 0 ? (
                        <ImageStripEmptyPlaceholder
                            invalid={imagesMissing}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <Label.Medium.Light.N60>
                                Drag and drop or click to add images
                            </Label.Medium.Light.N60>
                        </ImageStripEmptyPlaceholder>
                    ) : (
                        <div className="image-selector__thumbnails">
                            <ImageStrip
                                images={stripImages}
                                editable
                                busyIds={busyIds}
                                onReorder={onStripReorder}
                            />
                        </div>
                    )}
                    <input
                        className="image-selector__file-input"
                        ref={fileInputRef}
                        type="file"
                        onChange={(event) => {
                            addLocalImages(event.target.files);
                            event.target.value = "";
                        }}
                        style={{ display: "none" }}
                        multiple
                        accept={supportedImageAccept}
                    />
                </Flex.Column>
            </Flex.Column>
        </HandleDragAndDrop>
    );
}

export default React.memo(ImageSelector);
